/**
 * useAudience — аналитика «Аудитории» клиентского приложения (DC). Источник
 * правды — GET /api/audience (perm `clients`): по каждому app-человеку баланс/
 * заработок юникоинов, игры, брони+деньги, связи. Фильтр/сортировка — на клиенте.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export interface AudiencePerson {
  tg: number;
  name: string;
  username: string;
  avatarUrl: string;
  verified: number;
  linked_at: string;
  client_id: number | null;
  uc_balance: number;
  uc_earned: number;
  games: number;
  booked: number;
  money: number;
  invited: number;
  staff: number;
  tag_minor: number;
  tag_adult: number;
  tag_competitor: number;
  excluded: number;
  sabotage: number;
  blocked: number;
  /** 07.10: галочка «Согласен получать сообщения от бота»: 1 — поставил, 0 — отписался (/stop), null — ещё не ставил. */
  consent?: number | null;
  teamId: number | null;
  teamSize: number;
  relationId: number | null;
  relationSize: number;
  lastGame: { quest: string; date: string; time: string } | null;
}

export interface AudienceDetail {
  person: { tg: number; name: string; username: string; verified: number; linked_at: string; client_id: number | null; method: string };
  earned: { type: string; total: number; n: number }[];
  spent: { type: string; total: number; n: number }[];
  invitedBy: { tg: number; name: string; username: string } | null;
  invitedPeople: { tg: number; name: string; username: string }[];
  games: { bookingId: number; date: string; time: string; quest: string }[];
}

export interface UseAudienceResult {
  people: AudiencePerson[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  patchMeta: (tg: number, patch: AudienceMetaPatch) => Promise<void>;
  checkBlocks: () => Promise<{ checked: number; blocked: number }>;
}

export type AudienceMetaPatch = Partial<Pick<AudiencePerson, "tag_minor" | "tag_adult" | "tag_competitor" | "excluded" | "sabotage" | "staff">>;

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Ошибка";
}

export function useAudience(): UseAudienceResult {
  const [people, setPeople] = useState<AudiencePerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);
  const patchMeta = useCallback(async (tg: number, patch: AudienceMetaPatch) => {
    setPeople((prev) => prev.map((p) => (p.tg === tg ? { ...p, ...patch } : p)));
    try {
      await api(`/api/audience/${tg}/meta`, { method: "PATCH", body: patch });
    } catch (e) {
      setTrigger((n) => n + 1);
      throw e;
    }
  }, []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<{ results: AudiencePerson[] }>("/api/audience", { signal: ac.signal });
        if (ac.signal.aborted) return;
        setPeople(r.results ?? []);
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setError(errMsg(e));
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [trigger]);

  const checkBlocks = useCallback(async () => {
    return api<{ checked: number; blocked: number }>("/api/audience/check-blocks", { method: "POST" });
  }, []);

  return { people, loading, error, reload, patchMeta, checkBlocks };
}
